import { generateClient, get as restGet, post as restPost, put as restPut, del as restDel } from 'aws-amplify/api';
import { AUTH_TYPE, mapAuthMode } from './internal/authModeMap';
import { normalizeGraphqlError, normalizeRestError } from './internal/errorNormalizer';
import { createSubscriptionHandle, isSubscriptionDocument } from './internal/subscriptionHandle';

export { AUTH_TYPE };

// v6 không có Apollo cache nên `options.fetchPolicy` vô nghĩa, và `timeout` không còn là
// option hợp lệ của graphql(). Bỏ trước khi gọi để v6 không nhận key lạ.
const STRIP_KEYS = ['options', 'timeout'];

let client = null;

const getClient = () => {
    if (!client) {
        client = generateClient();
    }
    return client;
};

const prepareParams = params => {
    if (!params || typeof params !== 'object') {
        return params;
    }

    const prepared = { ...params };
    for (const key of STRIP_KEYS) {
        delete prepared[key];
    }
    if (prepared.authMode) {
        prepared.authMode = mapAuthMode(prepared.authMode);
    }
    return prepared;
};

const graphql = params => {
    const prepared = prepareParams(params);

    if (isSubscriptionDocument(params?.query)) {
        // v6 bỏ lớp `.value` khỏi payload subscription; dựng lại để 25 call site
        // `resData.value.data.X` trong 13 file không phải sửa.
        return createSubscriptionHandle(getClient().graphql(prepared), payload => ({ value: payload }));
    }

    return getClient()
        .graphql(prepared)
        .catch(err => Promise.reject(normalizeGraphqlError(err)));
};

const toRestOptions = init => {
    if (!init || typeof init !== 'object') {
        return undefined;
    }

    const options = {};
    if (init.queryStringParameters) {
        options.queryParams = init.queryStringParameters;
    }
    if (init.queryParams) {
        options.queryParams = { ...options.queryParams, ...init.queryParams };
    }
    if (init.headers) {
        options.headers = init.headers;
    }
    if (typeof init.body !== 'undefined') {
        options.body = init.body;
    }
    if (typeof init.withCredentials !== 'undefined') {
        options.withCredentials = init.withCredentials;
    }
    return Object.keys(options).length ? options : undefined;
};

/**
 * v2 (axios) trả thẳng body đã parse. v6 trả một operation phải `await .response` rồi
 * `await body.json()`. Bọc lại để consumer giữ nguyên `const data = await API.get(...)`.
 */
const callRest = async (operationFn, apiName, path, init) => {
    try {
        const { body } = await operationFn({ apiName, path, options: toRestOptions(init) }).response;
        const text = await body.text();
        if (!text) {
            return null;
        }
        try {
            return JSON.parse(text);
        } catch (e) {
            return text;
        }
    } catch (err) {
        return Promise.reject(await normalizeRestError(err));
    }
};

const get = (apiName, path, init) => callRest(restGet, apiName, path, init);

const post = (apiName, path, init) => callRest(restPost, apiName, path, init);

const put = (apiName, path, init) => callRest(restPut, apiName, path, init);

const del = (apiName, path, init) => callRest(restDel, apiName, path, init);

export default { graphql, get, post, put, del, AUTH_TYPE };
